import React, { useCallback } from 'react';

import { ActionIcon, Group, Paper, Skeleton, Text, Title, Tooltip, createStyles } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilterOff } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import HelpButton from 'components/HelpButton';
import dayjs from 'dayjs';
import useTrialMembers from 'hooks/reports/athletes/useTrialMembers';
import { useNavigate } from 'react-router-dom';
import { TrialMemberReport } from 'types';
import { setDateDifference } from 'utils/date-functions';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content auto max-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title dateRange clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content auto min-content min-content',
      gridTemplateAreas: `
        'backButton title clear download'
        'dateRange dateRange dateRange dateRange'
        `,
    },
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.sm,
      gridTemplateColumns: 'min-content auto min-content',
      gridTemplateAreas: `
        'backButton title clear'
        'dateRange dateRange dateRange'
        `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  dateRange: {
    gridArea: 'dateRange',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: TrialMemberReport): { [key: string]: any } => ({
  'Full Name': d.fullName,
  Email: d.email,
  Phone: d.phone,
  'Trial Start Date': dayjs(d.trialDate).format('YYYY-MM-DD'),
});

const TrialMembersPageHeader = ({
  initialSearchFiltersValue,
  searchFilters,
  setSearchFilters,
  orderingValue,
}: {
  initialSearchFiltersValue: SearchFilters;
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  orderingValue: string;
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();

  const { data, isLoading, isSuccess, isError } = useTrialMembers({
    ...searchFilters,
    dateStart: searchFilters.dateRange[0] ? searchFilters.dateRange[0].toISOString() : undefined,
    dateEnd: searchFilters.dateRange[1] ? searchFilters.dateRange[1].toISOString() : undefined,
  });

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const handleDateChange = (value: DatesRangeValue): void => {
    let adjustedDate: DatesRangeValue = [...value];
    if (!!value[1]) {
      const adjustedEndDate = value[1];
      adjustedDate[1] = setDateDifference(adjustedEndDate, 0, 0, 0, 23, 59, 59);
    }
    setSearchFilters(prev => ({ ...prev, dateRange: adjustedDate }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  const getTrialMemberCount = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Title order={isDesktop ? 1 : 3} color='red'>
          Error
        </Title>
      );
    }
    if (isSuccess && data.pages) {
      return (
        <Title order={isDesktop ? 1 : 3} color='gray'>
          {data.pages[0].data.count}
        </Title>
      );
    }
    return <></>;
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title} data-cy='trialMembersPageHeader'>
        <Title order={isDesktop ? 1 : 3}>Trial Members</Title>
        {getTrialMemberCount()}
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm'>
            {
              'All athletes that changed status from "Prospect" or "Visitor" or "Non-Paying Member" to "Trial" within the selected date range.'
            }
          </Text>
        </HelpButton>
      </Group>
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.dateRange}
        onChange={handleDateChange}
        valueFormat='MMMM DD, YYYY'
        type='range'
        allowSingleDateInRange
        maxDate={new Date()}
        required
        {...{ placeholder: 'Select date range' }}
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={useTrialMembers}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{
          ...searchFilters,
          ordering: orderingValue,
          dateStart: searchFilters.dateRange[0] ? searchFilters.dateRange[0].toISOString() : undefined,
          dateEnd: searchFilters.dateRange[1] ? searchFilters.dateRange[1].toISOString() : undefined,
        }}
        className={classes.download}
        reportType='trial-members'
        startDate={
          searchFilters.dateRange[0] ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD') : null
        }
        endDate={searchFilters.dateRange[1] ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD') : null}
      />
    </Paper>
  );
};

export default TrialMembersPageHeader;
